import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput,
  CommandItem, CommandList, CommandSeparator,
} from "@/components/ui/command";
import { DialogTitle, DialogDescription } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { api } from "@/lib/api";
import { FolderKanban, FileText, Users, NotebookPen } from "lucide-react";

export default function CommandPalette({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) { setQ(""); setResults(null); return; }
  }, [open]);

  useEffect(() => {
    if (!q || q.length < 2) { setResults(null); return; }
    const id = setTimeout(async () => {
      try { const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`); setResults(data); }
      catch { setResults(null); }
    }, 280);
    return () => clearTimeout(id);
  }, [q]);

  const go = (path) => { onOpenChange(false); navigate(path); };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <VisuallyHidden.Root>
        <DialogTitle>Ricerca globale</DialogTitle>
        <DialogDescription>Cerca tra pratiche, documenti, contatti e note</DialogDescription>
      </VisuallyHidden.Root>
      <CommandInput value={q} onValueChange={setQ} placeholder="Cerca pratiche, documenti, contatti, note..." data-testid="palette-input" />
      <CommandList>
        {!q && <CommandEmpty>Inizia a digitare per cercare nella piattaforma.</CommandEmpty>}
        {q && !results && <CommandEmpty>Caricamento...</CommandEmpty>}
        {results?.practices?.length > 0 && (
          <CommandGroup heading={`Pratiche (${results.practices.length})`}>
            {results.practices.map((p) => (
              <CommandItem key={p.id} onSelect={() => go(`/pratiche/${p.id}`)}>
                <FolderKanban className="w-4 h-4 mr-2 text-navy-600" />
                <span className="font-mono text-xs text-slate-500 mr-2">{p.numero_protocollo}</span>
                <span>{p.title}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results?.contacts?.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={`Contatti (${results.contacts.length})`}>
              {results.contacts.map((c) => (
                <CommandItem key={c.id} onSelect={() => go(`/contatti`)}>
                  <Users className="w-4 h-4 mr-2 text-navy-600" />
                  <span>{c.name}</span>
                  {c.email && <span className="ml-2 text-xs text-slate-400">{c.email}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
        {results?.documents?.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={`Documenti (${results.documents.length})`}>
              {results.documents.map((d) => (
                <CommandItem key={d.id} onSelect={() => go(`/documenti`)}>
                  <FileText className="w-4 h-4 mr-2 text-navy-600" />
                  <span>{d.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
        {results?.notes?.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={`Note (${results.notes.length})`}>
              {results.notes.map((n) => (
                <CommandItem key={n.id} onSelect={() => go(`/pratiche/${n.project_id}`)}>
                  <NotebookPen className="w-4 h-4 mr-2 text-navy-600" />
                  <span className="truncate">{n.content.slice(0, 80)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
